import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import { Tabs, Tab, Typography, Card, Dialog } from "@material-ui/core";
import {
  PageRoot,
  PageHeader,
  ResponseDueIcon,
  Icon,
  Button,
  TypeaheadSelect,
  Select
} from "../../components";
import RxTable, {
  Table,
  IColumn,
  TableDetailCard,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";

import routes from "../../constants/routes";

import {
  PaymentStatusMapper,
  RegulatoryInstrument,
  RegulatoryInstrumentStatus
} from "../../constants/interfaces";
import formatDate from "../../utils/formatDate";
import { regulatoryInstrumentActions } from "../../reducers/regulatoryInstrument";
import downloadFile from "../../utils/downloadFile";
import api from "../../utils/api";

const useStyles = makeStyles(theme => ({
  applyContainer: {
    alignItems: "center",
    display: "flex",
    flexFlow: "column",
    justifyContent: "center",
    marginTop: "94px",
    padding: theme.spacing(8)
  },
  applySubtitle: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  modalButtonWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2)
  },
  modalIcon: {
    marginRight: theme.spacing(1)
  },
  modalPaper: {
    padding: theme.spacing(4)
  },
  modalTitleWrapper: {
    alignItems: "center",
    display: "flex",
    marginBottom: theme.spacing(2)
  },
  paymentStatus: {
    color: theme.palette.secondary.main
  },
  select: {
    marginBottom: theme.spacing(1),
    width: "50%"
  },
  fullWidthSelect: {
    width: "100%",
    marginBottom: theme.spacing(1)
  },
  tabIcon: {
    marginBottom: "0 !important", // prevent MUI overwiting this
    marginRight: theme.spacing(1)
  },
  tabMode: {
    [theme.breakpoints.up("sm")]: {
      maxWidth: "calc(100vw - 320px)", // prevent obscuring filter & search buttons
      position: "absolute",
      zIndex: 2
    }
  },
  tabLabelIcon: {
    minHeight: "unset",
    padding: 0
  },
  tabWrapper: {
    alignItems: "center",
    textTransform: "unset",
    flexFlow: "row"
  },
  toolBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
  }
}));

export enum TabMode {
  ACTIVE = "active",
  APPLY = "apply",
  IN_PROGRESS = "inProgress",
  RENEW = "renew"
}

type Props = {
  classes?: any;
  error?: Error;
  getInstruments: () => Promise<void>;
  history: any;
  location: any;
  instruments: RegulatoryInstrument[];
  instrumentTypes: any;
  loading: boolean;
};

const RiTabs = ({
  tab,
  onChange,
  onTabHintClick
}: {
  tab: TabMode;
  onChange: (e: any, value: TabMode) => void;
  onTabHintClick: (dialog: { title: string; body: string }) => void;
}) => {
  const classes = useStyles();
  return (
    <Tabs
      className={classes.tabMode}
      onChange={onChange}
      value={tab}
      variant="fullWidth"
    >
      <Tab
        className={classes.tabWrapper}
        classes={{
          labelIcon: classes.tabLabelIcon,
          wrapper: classes.tabWrapper
        }}
        icon={
          tab === TabMode.ACTIVE ? (
            <Icon
              className={classes.tabIcon}
              color="disabled"
              onClick={() =>
                onTabHintClick({
                  body: "A list of active regulatory instruments.",
                  title: "Active Regulatory Instruments"
                })
              }
              name="info_outline"
            />
          ) : (
            undefined
          )
        }
        label="Active"
        value={TabMode.ACTIVE}
      />

      <Tab
        className={classes.tabWrapper}
        classes={{
          labelIcon: classes.tabLabelIcon,
          wrapper: classes.tabWrapper
        }}
        icon={
          tab === TabMode.RENEW ? (
            <Icon
              className={classes.tabIcon}
              color="disabled"
              onClick={() =>
                onTabHintClick({
                  body:
                    "A list of regulatory instruments that are available for renewal.",
                  title: "Renewing Regulatory Instruments"
                })
              }
              name="info_outline"
            />
          ) : (
            undefined
          )
        }
        label="Renewal required"
        value={TabMode.RENEW}
      />

      <Tab
        className={classes.tabWrapper}
        classes={{
          labelIcon: classes.tabLabelIcon,
          wrapper: classes.tabWrapper
        }}
        icon={
          tab === TabMode.APPLY ? (
            <Icon
              className={classes.tabIcon}
              color="disabled"
              onClick={() =>
                onTabHintClick({
                  body:
                    "Submit an application for a new regulatory instrument.",
                  title: "Apply for Regulatory Instrument"
                })
              }
              name="info_outline"
            />
          ) : (
            undefined
          )
        }
        label="Apply"
        value={TabMode.APPLY}
      />

      <Tab
        className={classes.tabWrapper}
        classes={{
          labelIcon: classes.tabLabelIcon,
          wrapper: classes.tabWrapper
        }}
        icon={
          tab === TabMode.IN_PROGRESS ? (
            <Icon
              className={classes.tabIcon}
              color="disabled"
              onClick={() =>
                onTabHintClick({
                  body: "A list of applications currently in progress.",
                  title: "Regulatory Instruments In Progress"
                })
              }
              name="info_outline"
            />
          ) : (
            undefined
          )
        }
        label="Applications in progress"
        value={TabMode.IN_PROGRESS}
      />
    </Tabs>
  );
};

const RegulatoryInstruments: React.FC<Props> = props => {
  const classes = useStyles();
  const [{ title, body }, setDialog] = useState({ title: "", body: "" });
  const params = new URLSearchParams(props.location.search);
  const initTab = params.get("tab") as TabMode | null;
  const [tab, setTab] = useState(
    initTab && Object.values(TabMode).includes(initTab)
      ? initTab
      : TabMode.ACTIVE
  );

  const columns = useMemo(() => {
    const generalColumns: IColumn[] = [
      {
        id: "legalEntity",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="legalEntity"
            key="legalEntity"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premises",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="premises"
            key="premises"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "riType",
        label: "RI Type",
        searchable: true,
        parse: cell => props.instrumentTypes[cell] || cell,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="riType"
            key="riType"
            label="RI Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      }
    ];

    if (tab === TabMode.ACTIVE) {
      return generalColumns.concat([
        {
          id: "riNumber",
          label: "RI Number",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="riNumber"
              key="riNumber"
              label="RI Number"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "nhinumber",
          label: "NHI Number",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="nhinumber"
              key="nhinumber"
              label="NHI Number"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "riStatus",
          label: "RI Status",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="riStatus"
              key="riStatus"
              label="RI Status"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "riStartDate",
          label: "Start Date",
          type: "date",
          searchable: true,
          parse: formatDate.condensed,
          renderCell: cell => <ResponseDueIcon dueDate={cell} />,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="riStartDate"
              key="riStartDate"
              label="Start Date"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    formatDate.condensed(fValue) === formatDate.condensed(cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "expiryDate",
          label: "Expiry Date",
          type: "date",
          parse: formatDate.condensed,
          searchable: true,
          renderCell: cell => <ResponseDueIcon dueDate={cell} />,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="expiryDate"
              key="expiryDate"
              label="Expiry Date"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    formatDate.condensed(fValue) === formatDate.condensed(cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        }
      ]);
    }

    if (tab === TabMode.RENEW) {
      return generalColumns.concat([
        {
          id: "type",
          label: "Application Type",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="type"
              key="type"
              label="Application Type"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "subtype",
          label: "Application Subtype",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="subtype"
              key="subtype"
              label="Application Subtype"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "expiryDate",
          label: "Expiry Date",
          type: "date",
          searchable: true,
          parse: formatDate.condensed,
          renderCell: cell => <ResponseDueIcon dueDate={cell} />,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="expiryDate"
              key="expiryDate"
              label="Expiry Date"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    formatDate.condensed(fValue) === formatDate.condensed(cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        }
      ]);
    }

    if (tab === TabMode.IN_PROGRESS) {
      return generalColumns.concat([
        {
          id: "type",
          label: "Application Type",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="type"
              key="type"
              label="Application Type"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "subtype",
          label: "Application Subtype",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="subtype"
              key="subtype"
              label="Application Subtype"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "nhinumber",
          label: "NHI Number",
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.select}
              id="nhinumber"
              key="nhinumber"
              label="NHI Number"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    fValue === cellValue
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "riDateApplicationReceived",
          label: "Date Application Received",
          type: "date",
          parse: formatDate.condensed,
          renderCell: cell => <ResponseDueIcon dueDate={cell} />,
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.fullWidthSelect}
              id="riDateApplicationReceived"
              key="riDateApplicationReceived"
              label="Date Application Received"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    formatDate.condensed(fValue) === formatDate.condensed(cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "riDateApplicationCompleted",
          label: "Date Application Completed",
          type: "date",
          parse: formatDate.condensed,
          renderCell: cell => <ResponseDueIcon dueDate={cell} />,
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.fullWidthSelect}
              id="riDateApplicationCompleted"
              key="riDateApplicationCompleted"
              label="Date Application Completed"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    formatDate.condensed(fValue) === formatDate.condensed(cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          )
        },
        {
          id: "riInvoiceStatus",
          label: "Payment Status",
          parse: status => PaymentStatusMapper[status] || status,
          searchable: true,
          filterWidget: (filterValue, onFilterChange, meta) => (
            <Select
              className={classes.fullWidthSelect}
              id="riInvoiceStatus"
              key="riInvoiceStatus"
              label="Payment Status"
              onChange={e =>
                onFilterChange({
                  value: e.target.value,
                  compare: (fValue: string, cellValue: string) =>
                    (PaymentStatusMapper[fValue] || fValue) === (PaymentStatusMapper[cellValue] || cellValue)
                })
              }
              options={meta.suggestions}
              value={filterValue || ""}
            />
          ),
          renderCell: cell => {
            const displayCell = PaymentStatusMapper[cell] || cell;
            return (
              <span
                className={
                  displayCell === "Payment Required"
                    ? classes.paymentStatus
                    : undefined
                }
              >
                {displayCell}
              </span>
            );
          }
        },
        {
          id: "hasInvoice",
          label: "Invoice",
          hide: true,
          meta: {
            renderItem: (cell: string, row: any) => {
              const caseId = row.cmAcmCaseIdentifier;
              const token = window.localStorage.getItem("token");
              const url = `/mcols/api/ri/downloadFile/Invoice?caseId=${caseId}&token=${token}`;
              if (cell === "Yes")
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "50%"
                    }}
                  >
                    <Typography color="textSecondary" variant="caption">
                      Invoice
                    </Typography>
                    <Icon
                      color="primary"
                      name="get_app"
                      style={{
                        cursor: "pointer"
                      }}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadFile({ url });
                      }}
                    />
                  </div>
                );
            }
          }
        }
      ]);
    }

    return [];
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useMemo<RegulatoryInstrument[]>(() => {
    if (tab === TabMode.ACTIVE)
      return props.instruments.filter(({ riStatus }) => {
        if (!riStatus) return false;
        return [
          RegulatoryInstrumentStatus.ACTIVE,
          RegulatoryInstrumentStatus.PROVISIONALLY_ACTIVE,
          RegulatoryInstrumentStatus.SURRENDER_INITIATED,
          RegulatoryInstrumentStatus.ACTIVE_CONTINUING_IN_FORCE
        ].includes(riStatus);
      });

    if (tab === TabMode.RENEW)
      return props.instruments.filter(({ riStatus }) => {
        if (!riStatus) return false;
        return [RegulatoryInstrumentStatus.APPLICATION_REQUIRED].includes(
          riStatus
        );
      });

    if (tab === TabMode.IN_PROGRESS)
      return props.instruments.filter(({ riStatus }) => {
        if (!riStatus) return false;
        return [
          RegulatoryInstrumentStatus.ONLINE_APPLICATION_RECEIVED,
          RegulatoryInstrumentStatus.APPLICATION_RECEIVED,
          RegulatoryInstrumentStatus.IN_PROGRESS,
          RegulatoryInstrumentStatus.ASSESSMENT_REQUIRED,
          RegulatoryInstrumentStatus.PEER_REVIEW_COMPLETED,
          RegulatoryInstrumentStatus.DECISION_REQUIRED,
          RegulatoryInstrumentStatus.RESPONSE_REQUIRED,
          RegulatoryInstrumentStatus.MANUAL_NOTIFICATION_REQUIRED
        ].includes(riStatus);
      });

    return [];
  }, [tab, props.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.getInstruments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader label={routes.REGULATORY_INSTRUMENTS.label} />

      <RiTabs
        onTabHintClick={setDialog}
        tab={tab}
        onChange={(e: any, value: TabMode) => setTab(value)}
      />
      {tab === TabMode.APPLY ? (
        <Card className={classes.applyContainer}>
          <Typography color="primary" variant="h5">
            Apply for a new regulatory instrument
          </Typography>
          <Typography
            className={classes.applySubtitle}
            color="textSecondary"
            variant="subtitle1"
          >
            Access the application page below to get started
          </Typography>
          <Button
            color="primary"
            id="apply"
            onClick={() => props.history.push(routes.APPLY_RI.url)}
          >
            Begin Application
          </Button>
        </Card>
      ) : (
        <RxTable>
          <div className={classes.toolBar}>
            <SearchBar />
            <CustomWidget
              compare={(next, prev) =>
                next.id === prev.id &&
                next.rawData === prev.rawData &&
                next.filters === prev.filters
              }
            >
              {({ dispatch, filters, columns, rawData }) => (
                <FilterBar
                  filters={filters}
                  id={tab}
                  dispatch={dispatch}
                  data={rawData}
                  columns={columns}
                />
              )}
            </CustomWidget>
          </div>

          <Table
            id={tab}
            columns={columns}
            loading={props.loading}
            sorting={
              tab === TabMode.IN_PROGRESS
                ? { sortDirection: "desc", id: "riDateApplicationReceived" }
                : { sortDirection: "asc", id: "premises" }
            }
            data={tableData}
            rowsPerPage={50}
            getRowId={row => row.cmAcmCaseIdentifier}
          />

          <TableDetailCard
            detailCardActions={[
              {
                label: "Amend",
                shouldHide: () => tab !== TabMode.ACTIVE,
                shouldDisable: cell => cell.canBeAmended === "No",
                onClick: ri => {
                  props.history.push(
                    `${routes.AMEND_RI.url}${ri.cmAcmCaseIdentifier}`
                  );
                }
              },
              {
                label: "Renew",
                shouldHide: () => tab !== TabMode.RENEW,
                onClick: ri => {
                  props.history.push(
                    `${routes.RENEW_RI.url}${ri.cmAcmCaseIdentifier}`
                  );
                }
              }
            ]}
            getData={async row => {
              const { data } = await api.get(
                `/ri/getUserRIDetails/${row.cmAcmCaseIdentifier}`
              );
              return data;
            }}
            title={row => row.premises}
            columns={columns}
          />
        </RxTable>
      )}

      <Dialog
        open={!!title}
        onClose={() => setDialog({ body: "", title: "" })}
        classes={{
          paper: classes.modalPaper
        }}
      >
        <div className={classes.modalTitleWrapper}>
          <Icon
            className={classes.modalIcon}
            color="primary"
            name="info_outline"
          />
          <Typography id="modal-title" color="primary" variant="h5">
            {title}
          </Typography>
        </div>
        <Typography>{body}</Typography>
        <div className={classes.modalButtonWrapper}>
          <Button id="done" onClick={() => setDialog({ body: "", title: "" })}>
            Done
          </Button>
        </div>
      </Dialog>
    </PageRoot>
  );
};

const mapStateToProps = ({ regulatoryInstrument }: any) => {
  return {
    error: regulatoryInstrument.error,
    instruments: regulatoryInstrument.instruments,
    loading: regulatoryInstrument.loading,
    instrumentTypes: regulatoryInstrument.instrumentTypes
  };
};

const mapDispatchToProps = {
  getInstruments: regulatoryInstrumentActions.getRegulatoryInstruments
};

export { RegulatoryInstruments };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RegulatoryInstruments);
